// ChallengeSuccessModal.tsx
import React, { useEffect, useState } from 'react';

interface ChallengeSuccessModalProps {
  challengeType: 'daily' | 'weekly';
  xpEarned: number;
  currentXP: number;
  currentLevel: number;
  onClose: () => void;
}

const ChallengeSuccessModal: React.FC<ChallengeSuccessModalProps> = ({
  challengeType,
  xpEarned,
  currentXP,
  currentLevel,
  onClose
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Trigger animation after mount
    setTimeout(() => setIsVisible(true), 50);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  const isDailyChallenge = challengeType === 'daily';

  return (
    <div
      onClick={handleClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999,  // ← Increased from 9999
        opacity: isVisible ? 1 : 0,
        transition: 'opacity 0.3s ease',
        padding: '20px'
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: window.innerWidth < 768 ? '16px' : '24px',
          padding: window.innerWidth < 768 ? '32px 24px' : '48px 40px',
          maxWidth: window.innerWidth < 768 ? '90vw' : '500px',
          width: '100%',
          textAlign: 'center',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          transform: isVisible ? 'scale(1)' : 'scale(0.8)',
          transition: 'transform 0.3s ease',
          position: 'relative',
          overflow: 'hidden',
          maxHeight: window.innerWidth < 768 ? '90vh' : 'auto',
          overflowY: window.innerWidth < 768 ? 'auto' : 'visible'
        }}
      >
        {/* Confetti Background Effect */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, ${isDailyChallenge ? '#FEF3C7' : '#EDE9FE'} 0%, white 100%)`,
          opacity: 0.5,
          zIndex: 0
        }} />

        {/* Content */}
        <div style={{ position: 'relative', zIndex: 1 }}>
          {/* Success Icon */}
          <div style={{
            fontSize: window.innerWidth < 768 ? '3rem' : '5rem',
            marginBottom: window.innerWidth < 768 ? '12px' : '16px',
            animation: 'bounce 0.6s ease'
          }}>
            🎉
          </div>

          {/* Title */}
          <h2 style={{
            fontSize: window.innerWidth < 768 ? '1.5rem' : '2rem',
            fontWeight: '800',
            margin: '0 0 8px 0',
            color: '#1a1a1a'
          }}>
            Challenge Complete!
          </h2>

          <div style={{
            fontSize: window.innerWidth < 768 ? '0.8rem' : '0.9rem',
            color: '#666',
            marginBottom: window.innerWidth < 768 ? '16px' : '24px',
            fontWeight: '600'
          }}>
            {isDailyChallenge ? '⏰ Daily Challenge' : '📚 Weekly Challenge'}
          </div>

          {/* XP Earned */}
          <div style={{
            background: `linear-gradient(135deg, ${isDailyChallenge ? '#F59E0B' : '#6C5CE7'}, ${isDailyChallenge ? '#D97706' : '#5B4BC4'})`,
            borderRadius: '16px',
            padding: window.innerWidth < 768 ? '16px' : '24px',
            marginBottom: window.innerWidth < 768 ? '16px' : '24px',
            boxShadow: `0 8px 20px ${isDailyChallenge ? 'rgba(245, 158, 11, 0.3)' : 'rgba(108, 92, 231, 0.3)'}`
          }}>
            <div style={{
              fontSize: window.innerWidth < 768 ? '0.75rem' : '0.9rem',
              color: 'rgba(255,255,255,0.9)',
              marginBottom: '8px',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}>
              XP Earned
            </div>
            <div style={{
              fontSize: window.innerWidth < 768 ? '2rem' : '3rem',
              fontWeight: '800',
              color: 'white',
              textShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}>
              +{xpEarned}
            </div>
          </div>

          {/* Current Progress */}
          <div style={{
            background: '#F8F9FA',
            borderRadius: '12px',
            padding: window.innerWidth < 768 ? '12px' : '16px',
            marginBottom: window.innerWidth < 768 ? '16px' : '24px'
          }}>
            <div style={{
              fontSize: window.innerWidth < 768 ? '0.75rem' : '0.85rem',
              color: '#666',
              marginBottom: '8px',
              fontWeight: '600'
            }}>
              Your Progress
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-around',
              alignItems: 'center'
            }}>
              <div>
                <div style={{ 
                  fontSize: window.innerWidth < 768 ? '1.25rem' : '1.5rem', 
                  fontWeight: '700', 
                  color: '#6C5CE7' 
                }}>
                  {currentLevel}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#999' }}>Level</div>
              </div>
              <div>
                <div style={{ 
                  fontSize: window.innerWidth < 768 ? '1.25rem' : '1.5rem', 
                  fontWeight: '700', 
                  color: '#10B981' 
                }}>
                  {currentXP.toLocaleString()}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#999' }}>Total XP</div>
              </div>
            </div>
          </div>

          {/* Continue Button */}
          <button
            onClick={handleClose}
            style={{
              background: `linear-gradient(135deg, ${isDailyChallenge ? '#F59E0B' : '#6C5CE7'}, ${isDailyChallenge ? '#D97706' : '#5B4BC4'})`,
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              padding: window.innerWidth < 768 ? '14px 24px' : '16px 32px',
              fontSize: window.innerWidth < 768 ? '0.9rem' : '1rem',
              fontWeight: '700',
              cursor: 'pointer',
              width: '100%',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              transition: 'transform 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            Continue Writing ✨
          </button>
        </div>

        <style>{`
          @keyframes bounce {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-20px); }
          }
        `}</style>
      </div>
    </div>
  );
};

export default ChallengeSuccessModal;